export interface SocialMediaItem {
  content: string;
  markdownContent?: string;
  image?: string;
  image_url?: string; // Adding this to match your API response
}

export const exportContent = async (socialMediaContent: SocialMediaItem[]) => {
  try {
    // Debug log
    console.log('Starting export with content:', socialMediaContent);
    
    // Create a zip file
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    
    // Array to store all promises for image fetching
    const imagePromises: Promise<void>[] = [];
    
    // Add content for each item
    socialMediaContent.forEach((item, index) => {
      // Add text content
      const fileName = `content_${index + 1}.md`;
      zip.file(fileName, item.markdownContent || item.content);
      
      // Get image URL from either image_url or image field
      const imageUrl = item.image_url || item.image;
      
      // Debug log
      console.log(`Processing item ${index + 1}, Image URL:`, imageUrl);
      
      // If there's an image, create a promise for fetching it
      if (imageUrl) {
        const imagePromise = (async () => {
          try {
            console.log(`Fetching image ${index + 1} from:`, imageUrl);
            
            let imageBlob: Blob;
            
            if (imageUrl.startsWith('data:')) {
              // Handle data URL
              imageBlob = dataURLtoBlob(imageUrl);
              console.log(`Processed data URL for image ${index + 1}`);
            } else {
              // Handle regular URL with CORS headers
              const response = await fetch(imageUrl, {
                headers: {
                  'Accept': 'image/*'
                },
                mode: 'cors'
              });
              
              if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
              }
              
              imageBlob = await response.blob();
              console.log(`Successfully fetched image ${index + 1}, size:`, imageBlob.size);
            }
            
            const extension = getImageExtension(imageUrl);
            zip.file(`image_${index + 1}.${extension}`, imageBlob);
            console.log(`Added image ${index + 1} to zip`);
          } catch (error) {
            console.error(`Failed to process image ${index + 1}:`, error);
          }
        })();
        
        imagePromises.push(imagePromise);
      }
    });
    
    // Wait for all images to be processed
    console.log('Waiting for all images to be processed...');
    await Promise.all(imagePromises);
    console.log('All images processed');
    
    // Generate the zip file
    console.log('Generating zip file...');
    const content = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6
      }
    });
    console.log('Zip file generated, size:', content.size);
    
    // Create download link and trigger download
    const url = window.URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'social_media_content.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('Download triggered');
    return true;
  } catch (error) {
    console.error('Error exporting content:', error);
    throw new Error('Failed to export content');
  }
};

// Helper function to convert data URL to blob
const dataURLtoBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new Blob([u8arr], { type: mime });
};

// Helper function to get image extension from URL or data URL
const getImageExtension = (url: string): string => {
  if (url.startsWith('data:')) {
    const mime = url.split(';')[0].split(':')[1];
    if (mime.includes('jpeg')) return 'jpg';
    if (mime.includes('png')) return 'png';
    if (mime.includes('gif')) return 'gif';
    return 'jpg';
  }
  
  const extension = url.split('.').pop()?.toLowerCase();
  return extension || 'jpg';
};